import { useEffect, useState } from 'react';
import {
  AlertOctagon,
  BarChart2,
  Loader2,
  Mic,
  ShieldAlert,
} from 'lucide-react';

import { getMeetingDigest } from '../../services/meetingsApi';

/*
 * The meeting at a glance.
 *
 * The full summary is twenty fields of prose. It is genuinely useful once
 * somebody sits down to read it, and almost nobody does — a person opening
 * yesterday's standup wants to know whether anything needs them, and answering
 * that from six paragraphs takes longer than having attended.
 *
 * So this answers only that, in one screen, and leaves the full summary one
 * click away. Nothing is removed.
 *
 * Charts are inline SVG on purpose. Three small charts do not justify a
 * charting dependency, its bundle, or its theming quarrel with Tailwind.
 */

const TONE = {
  NEUTRAL: 'text-zinc-100',
  WARN: 'text-amber-300',
  BAD: 'text-red-300',
};

const BAND_COLOR = {
  CRITICAL: '#f87171',
  HIGH: '#fb923c',
  MEDIUM: '#fbbf24',
  LOW: '#a3a3a3',
  FIRM: '#4ade80',
  CONDITIONAL: '#fbbf24',
  ASPIRATIONAL: '#a3a3a3',
};

const Tile = ({ tile }) => (
  <div className="flex min-w-0 flex-col gap-0.5 px-3 py-2">
    <span
      className={`text-2xl font-semibold tabular-nums leading-none ${
        TONE[tile.tone] || TONE.NEUTRAL
      }`}
    >
      {tile.value}
    </span>
    <span className="truncate text-[11px] leading-tight text-zinc-400">
      {tile.label}
    </span>
    {tile.hint ? (
      <span className="truncate text-[10px] leading-tight text-zinc-500">
        {tile.hint}
      </span>
    ) : null}
  </div>
);

/* Horizontal bars. Labels sit outside the bar so a 3% share is still readable —
 * text inside a short bar is the usual way these become unreadable. */
const BarRow = ({ label, value, pct, color }) => (
  <div className="flex items-center gap-2">
    <span className="w-20 shrink-0 truncate text-[11px] text-zinc-400">
      {label}
    </span>
    <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-zinc-700/40">
      <div
        className="h-full rounded-full transition-all"
        style={{
          width: `${Math.max(2, Math.round(pct * 100))}%`,
          background: color,
        }}
      />
    </div>
    <span className="w-9 shrink-0 text-right text-[11px] tabular-nums text-zinc-400">
      {value}
    </span>
  </div>
);

const Panel = ({ icon: Icon, title, children }) => (
  <div className="min-w-0 rounded-xl border border-zinc-700/30 bg-zinc-900/50 p-3">
    <div className="mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-zinc-500">
      <Icon size={12} />
      {title}
    </div>
    <div className="flex flex-col gap-1.5">{children}</div>
  </div>
);

/* Sentiment as a dot plot, not a line.
 *
 * These are per-person readings from a single meeting. Drawing them against an
 * x-axis would imply a trend over time that the data does not contain — the
 * spread is the whole story, so the chart shows the spread. */
const SentimentSpread = ({ scores }) => {
  if (!scores?.length) return null;
  const mean = scores.reduce((sum, value) => sum + value, 0) / scores.length;
  return (
    <div className="flex flex-col gap-1.5">
      <svg
        viewBox="0 0 100 22"
        className="h-6 w-full"
        role="img"
        aria-label={`Sentiment spread across ${scores.length} speakers`}
      >
        <line
          x1="0"
          y1="11"
          x2="100"
          y2="11"
          stroke="#3f3f46"
          strokeWidth="0.5"
        />
        <line
          x1="50"
          y1="4"
          x2="50"
          y2="18"
          stroke="#52525b"
          strokeWidth="0.5"
        />
        {scores.map((score, index) => (
          <circle
            key={index}
            cx={(score + 1) * 50}
            cy="11"
            r="2.5"
            fill={
              score < -0.2 ? '#f87171' : score > 0.2 ? '#4ade80' : '#a1a1aa'
            }
            opacity="0.85"
          />
        ))}
      </svg>
      <div className="flex justify-between text-[10px] text-zinc-500">
        <span>negative</span>
        <span className="tabular-nums">avg {mean.toFixed(2)}</span>
        <span>positive</span>
      </div>
    </div>
  );
};

const TAG_TONE = {
  BLOCKER: 'bg-red-500/15 text-red-300',
  STALLED: 'bg-amber-500/15 text-amber-300',
  UNVERIFIED: 'bg-amber-500/15 text-amber-300',
  CONDITIONAL: 'bg-amber-500/15 text-amber-300',
  FIRM: 'bg-emerald-500/15 text-emerald-300',
};

/* One record per line. The glance view exists because nobody reads the long
 * summary, so a row that needs a second line to make sense has already failed
 * at the job — the text is truncated server-side and never wraps here. */
const RecordRow = ({ row }) => (
  <div className="flex items-baseline gap-2 border-b border-zinc-700/20 py-1.5 last:border-b-0">
    {row.tag ? (
      <span
        className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
          TAG_TONE[row.tag] || 'bg-zinc-700/30 text-zinc-400'
        }`}
      >
        {row.tag}
      </span>
    ) : null}
    <span className="min-w-0 flex-1 truncate text-xs text-zinc-200">
      {row.text}
      {/* What was folded onto this row instead of printed as its own: the
          promise answering this blocker, the related risk on the same
          deliverable. Dimmer than the text, on the same line, so one subject
          stays one line. */}
      {row.note ? (
        <span className="ml-2 text-[11px] text-zinc-500">{row.note}</span>
      ) : null}
    </span>
    {row.who ? (
      <span className="shrink-0 text-[11px] text-zinc-400">{row.who}</span>
    ) : null}
    {row.when ? (
      <span className="shrink-0 text-[11px] text-zinc-500">{row.when}</span>
    ) : null}
  </div>
);

const RecordSection = ({ section }) => (
  <div className="min-w-0">
    <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-zinc-500">
      {section.title}
    </div>
    {section.rows.map((row, index) => (
      <RecordRow key={`${section.key}-${index}`} row={row} />
    ))}
    {section.more > 0 ? (
      <div className="pt-1 text-[11px] text-zinc-500">+{section.more} more</div>
    ) : null}
  </div>
);

const MeetingGlance = ({ accessToken, eventId }) => {
  const [digest, setDigest] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    if (!accessToken || !eventId) {
      setState('idle');
      return undefined;
    }
    let cancelled = false;
    setState('loading');
    getMeetingDigest(accessToken, eventId)
      .then((response) => {
        if (cancelled) return;
        setDigest(response?.digest || null);
        setState('ready');
      })
      .catch(() => {
        // A missing digest is not an error worth shouting about: the full
        // summary below is still there, and that is what used to be the whole
        // page. Fail quiet, not loud.
        if (!cancelled) setState('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, eventId]);

  if (state === 'loading') {
    return (
      <div className="mb-4 flex items-center gap-2 rounded-2xl border border-zinc-700/30 bg-zinc-900/60 px-4 py-6 text-xs text-zinc-500">
        <Loader2 size={13} className="animate-spin" />
        Building the glance view…
      </div>
    );
  }
  if (state !== 'ready' || !digest) return null;

  const airtimeMax = Math.max(
    ...(digest.airtime || []).map((r) => r.pct),
    0.01,
  );
  const riskTotal = (digest.risk_bands || []).reduce((s, b) => s + b.count, 0);
  const firmTotal = (digest.commitment_firmness || []).reduce(
    (s, b) => s + b.count,
    0,
  );
  const hasCharts =
    digest.airtime?.length ||
    digest.risk_bands?.length ||
    digest.commitment_firmness?.length ||
    digest.sentiment_trend?.length;

  return (
    <div className="mb-5 overflow-hidden rounded-2xl border border-zinc-700/30 bg-zinc-900/60">
      {/* Headline — the most consequential thing in the meeting, chosen by
          severity rather than by position in the transcript. */}
      <div className="border-b border-zinc-700/30 px-4 py-3.5">
        <div className="mb-1 text-[11px] uppercase tracking-wide text-zinc-500">
          {digest.subline}
        </div>
        <div className="text-[15px] font-medium leading-snug text-zinc-100">
          {digest.headline}
        </div>
      </div>

      {/* Counts */}
      <div className="flex flex-wrap divide-x divide-zinc-700/30 border-b border-zinc-700/30">
        {(digest.tiles || []).map((tile) => (
          <Tile key={tile.key} tile={tile} />
        ))}
      </div>

      {/* The line that decides whether anybody opens the meeting at all. */}
      {digest.needs_attention > 0 && (
        <div className="flex items-start gap-2 border-b border-zinc-700/30 bg-amber-500/[0.07] px-4 py-2.5">
          <AlertOctagon size={13} className="mt-0.5 shrink-0 text-amber-400" />
          <div className="min-w-0 text-xs text-amber-200/90">
            <span className="font-medium">
              {digest.needs_attention} waiting on a person
            </span>
            {digest.attention_detail?.length ? (
              <span className="text-amber-200/60">
                {' — '}
                {digest.attention_detail.join(' · ')}
              </span>
            ) : null}
          </div>
        </div>
      )}

      {digest.sections?.length > 0 && (
        <div className="flex flex-col gap-4 border-b border-zinc-700/30 px-4 py-3.5">
          {digest.sections.map((section) => (
            <RecordSection key={section.key} section={section} />
          ))}
        </div>
      )}

      {hasCharts ? (
        <div className="grid gap-2.5 p-3 sm:grid-cols-2 lg:grid-cols-3">
          {digest.airtime?.length > 0 && (
            <Panel icon={Mic} title="Airtime">
              {digest.airtime.map((row, index) => (
                <BarRow
                  key={`${row.person_id}-${index}`}
                  label={row.display_name}
                  value={`${Math.round(row.pct * 100)}%`}
                  pct={row.pct / airtimeMax}
                  color="#60a5fa"
                />
              ))}
            </Panel>
          )}

          {digest.risk_bands?.length > 0 && (
            <Panel icon={ShieldAlert} title="Risk register">
              {digest.risk_bands.map((band) => (
                <BarRow
                  key={band.band}
                  label={band.band.toLowerCase()}
                  value={band.count}
                  pct={band.count / riskTotal}
                  color={BAND_COLOR[band.band] || '#a3a3a3'}
                />
              ))}
            </Panel>
          )}

          {digest.commitment_firmness?.length > 0 && (
            <Panel icon={BarChart2} title="How firm the promises are">
              {digest.commitment_firmness.map((band) => (
                <BarRow
                  key={band.band}
                  label={band.band.toLowerCase()}
                  value={band.count}
                  pct={band.count / firmTotal}
                  color={BAND_COLOR[band.band] || '#a3a3a3'}
                />
              ))}
            </Panel>
          )}

          {digest.sentiment_trend?.length > 1 && (
            <Panel icon={BarChart2} title="Sentiment across the room">
              <SentimentSpread scores={digest.sentiment_trend} />
            </Panel>
          )}
        </div>
      ) : null}

      {digest.source === 'LLM_SUMMARY' && (
        <div className="border-t border-zinc-700/30 px-4 py-2 text-[11px] text-zinc-500">
          This meeting has not run through the extraction pipeline, so the
          glance view is limited to what the summary itself records.
        </div>
      )}
    </div>
  );
};

export default MeetingGlance;
